import { useState, useEffect, useMemo, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshIcon from "@mui/icons-material/Refresh";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Navigate } from "react-router-dom";

import Navbar from "../../components/Navbar";
import MultiSelectCheckbox from "../../components/MultiSelectCheckbox";
import zipzyAdminApiClient from "../../apiClients/zipzyAdminApiClient";
import { DEFAULT_LOCALITY_IDS } from "../../constants";

// a driver's vehicle can only be changed while they are offline, the backend rejects the
// change otherwise
const OFFLINE_STATUSES = ["MANUAL_OFFLINE", "AUTO_OFFLINE"];

const VEHICLE_OPTION_GROUPS = ["Current", "Unassigned", "With Other Drivers"];

function isDriverOffline(driver) {
  return OFFLINE_STATUSES.includes(driver.status);
}

function formatStatus(status) {
  if (status === "UNAVAILABLE_HEADING_BACK_TO_LOCALITY") {
    return "Heading Back";
  }

  return status
    .split("_")
    .map((w) => w[0] + w.slice(1).toLowerCase())
    .join(" ");
}

function getStatusColor(status) {
  if (status === "IDLE") {
    return "success";
  }
  if (status === "IN_RIDE") {
    return "warning";
  }
  if (status === "UNAVAILABLE_HEADING_BACK_TO_LOCALITY") {
    return "secondary";
  }
  return "default";
}

function getErrorMessage(error) {
  return error?.userFriendlyMessage ?? error?.userFriendlyError?.detail ?? error?.code ?? "Something went wrong.";
}

function formatVehicle(vehicle) {
  return vehicle ? vehicle.licensePlate : "No vehicle";
}

function DriverRow({
  driver,
  localityName,
  vehicleOptions,
  currentVehicle,
  originalVehicle,
  isChanged,
  isHighlighted,
  onVehicleChange,
  onSwapClick,
}) {
  const isEditable = isDriverOffline(driver);

  const autocomplete = (
    <Autocomplete
      sx={{ width: 280 }}
      size="small"
      disabled={!isEditable}
      options={vehicleOptions}
      value={currentVehicle}
      onChange={(event, newValue) => onVehicleChange(newValue ? newValue._id : null)}
      groupBy={(option) => option.group}
      getOptionLabel={(option) => option.licensePlate}
      getOptionDisabled={(option) =>
        option.holder !== null && option.holder._id !== driver._id && !isDriverOffline(option.holder)
      }
      isOptionEqualToValue={(option, value) => option._id === value._id}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        const isWithOtherDriver = option.holder !== null && option.holder._id !== driver._id;

        return (
          <li key={key} {...optionProps}>
            <ListItemText
              primary={option.licensePlate}
              secondary={
                isWithOtherDriver
                  ? `${option.type} · with ${option.holder.fullName}${
                      isDriverOffline(option.holder) ? "" : ` (${formatStatus(option.holder.status)})`
                    }`
                  : option.type
              }
            />
          </li>
        );
      }}
      renderInput={(params) => <TextField {...params} placeholder="No vehicle" />}
    />
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1.5,
        borderColor: isHighlighted ? "error.main" : isChanged ? "primary.main" : undefined,
        borderWidth: isHighlighted || isChanged ? 2 : 1,
      }}
    >
      <Box sx={{ flex: "1 1 200px", textAlign: "left" }}>
        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
          {driver.fullName}
        </Typography>
        {localityName && (
          <Typography variant="caption" color="text.secondary">
            {localityName}
          </Typography>
        )}
      </Box>

      <Chip label={formatStatus(driver.status)} color={getStatusColor(driver.status)} size="small" />

      <Box sx={{ textAlign: "left" }}>
        {isEditable ? (
          autocomplete
        ) : (
          <Tooltip title="Driver must be offline to change vehicle">
            <span>{autocomplete}</span>
          </Tooltip>
        )}
        {isChanged && (
          <Typography variant="caption" color="primary" sx={{ display: "block", mt: 0.5 }}>
            Was: {formatVehicle(originalVehicle)}
          </Typography>
        )}
      </Box>

      <Tooltip title="Swap vehicle with another driver">
        <span>
          <IconButton disabled={!isEditable} onClick={onSwapClick}>
            <SwapHorizIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Paper>
  );
}

export default function VehicleAssignmentPage() {
  const isLoggedIn = zipzyAdminApiClient.isLoggedIn();

  const [allLocalities, setAllLocalities] = useState([]);
  const [selectedLocalityIds, setSelectedLocalityIds] = useState(DEFAULT_LOCALITY_IDS);

  const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);
  const [assignmentRows, setAssignmentRows] = useState([]);
  const [unassignedVehicles, setUnassignedVehicles] = useState([]);

  // driverId -> vehicleId (null for no vehicle), only for drivers whose vehicle differs from
  // the one fetched from the server
  const [pendingVehicleIdByDriverId, setPendingVehicleIdByDriverId] = useState({});

  const [statusAlert, setStatusAlert] = useState(null);
  const [highlightedDriverIds, setHighlightedDriverIds] = useState([]);
  const [swapSourceDriverId, setSwapSourceDriverId] = useState(null);
  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // ignores responses of fetches that were superseded by a newer one
  const latestFetchIdRef = useRef(0);

  const localityNameById = useMemo(
    () => Object.fromEntries(allLocalities.map((locality) => [locality._id, locality.name])),
    [allLocalities]
  );

  const driversById = useMemo(
    () => Object.fromEntries(assignmentRows.map((row) => [row.driver._id, row.driver])),
    [assignmentRows]
  );

  const originalVehicleIdByDriverId = useMemo(
    () => Object.fromEntries(assignmentRows.map((row) => [row.driver._id, row.vehicle ? row.vehicle._id : null])),
    [assignmentRows]
  );

  // the vehicles held by the drivers of the selected localities, and the unassigned ones
  const vehiclesById = useMemo(() => {
    const vehicles = [...assignmentRows.filter((row) => row.vehicle).map((row) => row.vehicle), ...unassignedVehicles];
    return Object.fromEntries(vehicles.map((vehicle) => [vehicle._id, vehicle]));
  }, [assignmentRows, unassignedVehicles]);

  const getCurrentVehicleId = (driverId) =>
    driverId in pendingVehicleIdByDriverId ? pendingVehicleIdByDriverId[driverId] : originalVehicleIdByDriverId[driverId];

  const holderByVehicleId = {};
  for (const row of assignmentRows) {
    const vehicleId = getCurrentVehicleId(row.driver._id);
    if (vehicleId) {
      holderByVehicleId[vehicleId] = row.driver;
    }
  }

  const pendingDriverIds = Object.keys(pendingVehicleIdByDriverId);
  const hasPendingChanges = pendingDriverIds.length > 0;

  const numDriversWithVehicle = assignmentRows.filter((row) => getCurrentVehicleId(row.driver._id)).length;
  const numUnassignedVehicles = Object.keys(vehiclesById).filter((vehicleId) => !holderByVehicleId[vehicleId]).length;

  const getVehicleOptionsForDriver = (driverId) =>
    Object.values(vehiclesById)
      .map((vehicle) => {
        const holder = holderByVehicleId[vehicle._id] ?? null;
        const group = holder === null ? "Unassigned" : holder._id === driverId ? "Current" : "With Other Drivers";
        return { ...vehicle, holder: holder, group: group };
      })
      .sort(
        (a, b) =>
          VEHICLE_OPTION_GROUPS.indexOf(a.group) - VEHICLE_OPTION_GROUPS.indexOf(b.group) ||
          a.licensePlate.localeCompare(b.licensePlate)
      );

  // applies the given driverId -> vehicleId changes on top of the pending ones; changes that
  // bring a driver back to their fetched vehicle are dropped
  const applyVehicleChanges = (vehicleIdByDriverId) => {
    setPendingVehicleIdByDriverId((previous) => {
      const next = { ...previous };
      for (const [driverId, vehicleId] of Object.entries(vehicleIdByDriverId)) {
        if (vehicleId === originalVehicleIdByDriverId[driverId]) {
          delete next[driverId];
        } else {
          next[driverId] = vehicleId;
        }
      }
      return next;
    });
  };

  const handleVehicleChange = (driverId, vehicleId) => {
    const changes = { [driverId]: vehicleId };

    // the vehicle is taken from the driver currently holding it
    const holder = vehicleId ? holderByVehicleId[vehicleId] : null;
    if (holder && holder._id !== driverId) {
      changes[holder._id] = null;
    }

    applyVehicleChanges(changes);
  };

  const handleSwap = (driverIdA, driverIdB) => {
    applyVehicleChanges({
      [driverIdA]: getCurrentVehicleId(driverIdB),
      [driverIdB]: getCurrentVehicleId(driverIdA),
    });
    setSwapSourceDriverId(null);
  };

  const fetchFilterInfoFromApi = async () => {
    const response = await zipzyAdminApiClient.getFilterInfo();
    if (!response.success) {
      window.alert(getErrorMessage(response.error));
      return;
    }

    setAllLocalities(response.data.localities);
  };

  // with keepPendingChanges, the pending changes survive the refetch, minus the ones that now
  // match the fetched vehicle
  const fetchAssignmentsFromApi = async ({ keepPendingChanges = false } = {}) => {
    const fetchId = ++latestFetchIdRef.current;
    setIsLoadingAssignments(true);

    const response = await zipzyAdminApiClient.getVehicleAssignments({ localityIds: selectedLocalityIds });
    if (fetchId !== latestFetchIdRef.current) {
      return;
    }

    setIsLoadingAssignments(false);
    if (!response.success) {
      window.alert(getErrorMessage(response.error));
      return;
    }

    const rows = response.data.assignments;
    setAssignmentRows(rows);
    setUnassignedVehicles(response.data.unassignedVehicles);

    if (!keepPendingChanges) {
      setPendingVehicleIdByDriverId({});
      return;
    }

    const fetchedVehicleIdByDriverId = Object.fromEntries(
      rows.map((row) => [row.driver._id, row.vehicle ? row.vehicle._id : null])
    );
    setPendingVehicleIdByDriverId((previous) =>
      Object.fromEntries(
        Object.entries(previous).filter(
          ([driverId, vehicleId]) =>
            driverId in fetchedVehicleIdByDriverId && fetchedVehicleIdByDriverId[driverId] !== vehicleId
        )
      )
    );
  };

  const handleLocalitiesChange = (newLocalityIds) => {
    if (hasPendingChanges && !window.confirm("Changing the localities will discard your unsaved changes. Continue?")) {
      return;
    }

    setPendingVehicleIdByDriverId({});
    setHighlightedDriverIds([]);
    setStatusAlert(null);
    setSelectedLocalityIds(newLocalityIds);
  };

  const handleRefreshClick = () => {
    if (hasPendingChanges && !window.confirm("Refreshing will discard your unsaved changes. Continue?")) {
      return;
    }

    setHighlightedDriverIds([]);
    setStatusAlert(null);
    fetchAssignmentsFromApi();
  };

  const handleDiscardClick = () => {
    if (!window.confirm("Discard all unsaved changes?")) {
      return;
    }

    setPendingVehicleIdByDriverId({});
    setHighlightedDriverIds([]);
  };

  const handleSaveClick = async () => {
    setIsSaving(true);

    const assignments = pendingDriverIds.map((driverId) => ({
      driverId: driverId,
      vehicleId: pendingVehicleIdByDriverId[driverId],
    }));
    const response = await zipzyAdminApiClient.updateVehicleAssignments(assignments);

    setIsSaving(false);
    setIsReviewDialogOpen(false);

    if (response.success) {
      setStatusAlert({ severity: "success", message: `Saved ${assignments.length} vehicle assignment change(s).` });
      setHighlightedDriverIds([]);
      await fetchAssignmentsFromApi();
      return;
    }

    if (response.error?.code === "drivers_not_offline") {
      const drivers = response.error.drivers ?? [];
      setStatusAlert({
        severity: "error",
        message: `Nothing was saved. These drivers must be offline first: ${drivers
          .map((driver) => `${driver.fullName} (${formatStatus(driver.status)})`)
          .join(", ")}.`,
      });
      setHighlightedDriverIds(drivers.map((driver) => driver._id));
      await fetchAssignmentsFromApi({ keepPendingChanges: true });
      return;
    }

    if (response.error?.code === "vehicle_assigned_to_unlisted_driver") {
      setStatusAlert({
        severity: "error",
        message:
          "Nothing was saved. The assignments were changed elsewhere in the meantime, they have been refreshed, please review your changes and save again.",
      });
      await fetchAssignmentsFromApi({ keepPendingChanges: true });
      return;
    }

    window.alert(getErrorMessage(response.error));
  };

  useEffect(() => {
    document.title = "Vehicle Assignment - Zipzy Admin";

    if (!isLoggedIn) {
      return;
    }

    fetchFilterInfoFromApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    fetchAssignmentsFromApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocalityIds]);

  if (!isLoggedIn) {
    return <Navigate to="/auth/login" replace />;
  }

  const swapSourceDriver = swapSourceDriverId ? driversById[swapSourceDriverId] : null;
  const swapCandidateDrivers = swapSourceDriver
    ? assignmentRows
        .map((row) => row.driver)
        .filter(
          (driver) =>
            driver._id !== swapSourceDriver._id &&
            isDriverOffline(driver) &&
            (getCurrentVehicleId(driver._id) || getCurrentVehicleId(swapSourceDriver._id))
        )
    : [];

  // the locality is shown on each row unless exactly one locality is selected
  const shouldShowLocality = selectedLocalityIds.length !== 1;

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="center"
      minHeight="100vh"
      textAlign="center"
      sx={{
        pb: 12,
      }}
    >
      <Navbar />

      <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
        Vehicle Assignment
      </Typography>

      <MultiSelectCheckbox
        label="Select Localities"
        options={allLocalities}
        value={selectedLocalityIds}
        onChange={handleLocalitiesChange}
      />

      <Box
        sx={{
          width: "80%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {assignmentRows.length} drivers · {numDriversWithVehicle} with a vehicle · {numUnassignedVehicles} unassigned
          vehicles
        </Typography>

        <IconButton onClick={handleRefreshClick} disabled={isLoadingAssignments}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {statusAlert && (
        <Alert severity={statusAlert.severity} onClose={() => setStatusAlert(null)} sx={{ width: "80%", mb: 2, textAlign: "left" }}>
          {statusAlert.message}
        </Alert>
      )}

      {isLoadingAssignments ? (
        <CircularProgress sx={{ mt: 4 }} />
      ) : (
        <Box sx={{ width: "80%", display: "flex", flexDirection: "column", gap: 1 }}>
          {assignmentRows.length === 0 && (
            <Typography variant="body1" color="text.secondary" sx={{ mt: 4 }}>
              No drivers in the selected localities.
            </Typography>
          )}

          {assignmentRows.map((row) => {
            const driverId = row.driver._id;
            const currentVehicleId = getCurrentVehicleId(driverId);
            const vehicleOptions = getVehicleOptionsForDriver(driverId);

            return (
              <DriverRow
                key={driverId}
                driver={row.driver}
                localityName={shouldShowLocality ? localityNameById[row.driver.locality] : null}
                vehicleOptions={vehicleOptions}
                currentVehicle={vehicleOptions.find((option) => option._id === currentVehicleId) ?? null}
                originalVehicle={row.vehicle}
                isChanged={driverId in pendingVehicleIdByDriverId}
                isHighlighted={highlightedDriverIds.includes(driverId)}
                onVehicleChange={(vehicleId) => handleVehicleChange(driverId, vehicleId)}
                onSwapClick={() => setSwapSourceDriverId(driverId)}
              />
            );
          })}
        </Box>
      )}

      {hasPendingChanges && (
        <Paper
          elevation={6}
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            p: 2,
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography variant="body1">{pendingDriverIds.length} change(s)</Typography>
          <Button variant="outlined" onClick={handleDiscardClick}>
            Discard
          </Button>
          <Button variant="contained" onClick={() => setIsReviewDialogOpen(true)}>
            Review & Save
          </Button>
        </Paper>
      )}

      <Dialog open={swapSourceDriver !== null} onClose={() => setSwapSourceDriverId(null)} fullWidth maxWidth="xs">
        <DialogTitle>Swap vehicle of {swapSourceDriver?.fullName}</DialogTitle>
        <DialogContent>
          {swapCandidateDrivers.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No offline drivers to swap with.
            </Typography>
          ) : (
            <List>
              {swapCandidateDrivers.map((driver) => (
                <ListItemButton key={driver._id} onClick={() => handleSwap(swapSourceDriver._id, driver._id)}>
                  <ListItemText
                    primary={driver.fullName}
                    secondary={formatVehicle(vehiclesById[getCurrentVehicleId(driver._id)])}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSwapSourceDriverId(null)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isReviewDialogOpen} onClose={() => !isSaving && setIsReviewDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Review Changes</DialogTitle>
        <DialogContent>
          <List dense>
            {pendingDriverIds.map((driverId) => (
              <ListItemText
                key={driverId}
                primary={driversById[driverId]?.fullName}
                secondary={`${formatVehicle(vehiclesById[originalVehicleIdByDriverId[driverId]])} → ${formatVehicle(
                  vehiclesById[pendingVehicleIdByDriverId[driverId]]
                )}`}
              />
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsReviewDialogOpen(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveClick} disabled={isSaving}>
            {isSaving ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
